import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useAuth } from './contexts/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './pages/Auth/Login';
import Dashboard from './pages/Dashboard/Dashboard';
import AgentsPage from './pages/Agents/AgentsPage';
import PropertiesPage from './pages/Properties/PropertiesPage';
import ClientsPage from './pages/Clients/ClientsPage';
import ProjectsPage from './pages/Projects/ProjectsPage';
import ActivitiesPage from './pages/Activities/ActivitiesPage';
import MapPage from './pages/Map/MapPage';
import NotificationsPage from './pages/Notifications/NotificationsPage';
import PendingActionsPage from './pages/PendingActions/PendingActionsPage';
import BuildingCaretakersPage from './pages/BuildingCaretakers/BuildingCaretakersPage';
import SharedPropertyView from './pages/SharedPropertyView';
import PublicPropertyCreationPage from './pages/PublicPropertyCreationPage';
import LoadingScreen from './components/Common/LoadingScreen';
import BrokersPage from './pages/Brokers/BrokersPage';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      refetchOnWindowFocus: false,
      refetchOnMount: true,
    },
  },
});

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <QueryClientProvider client={queryClient}>
      <Box sx={{ display: 'flex', minHeight: '100vh' }}>
        <Routes>
          {/* Public routes - NO authentication required */}
          <Route path="/shared/property/:token" element={<SharedPropertyView />} />
          <Route path="/create-property/:token" element={<PublicPropertyCreationPage />} />
          
          <Route
            path="/login"
            element={
              isAuthenticated ? <Navigate to="/" replace /> : <Login />
            }
          />
          <Route
            path="/"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="pending-actions" element={<PendingActionsPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            <Route path="agents" element={<AgentsPage />} />
            <Route path="properties" element={<PropertiesPage />} />
            <Route path="clients" element={<ClientsPage />} />
            <Route path="projects" element={<ProjectsPage />} />
            <Route path="activities" element={<ActivitiesPage />} />
            <Route path="map" element={<MapPage />} />
            <Route path="building-caretakers" element={<BuildingCaretakersPage />} />
            <Route path="brokers" element={<BrokersPage />} />
          </Route>
        </Routes>
      </Box>
    </QueryClientProvider>
  );
};

export default App;
